import {
  type Questionario as DadosQuestionario,
  type EstadoCivil,
  type Questionario,
  questionarioSchema,
  type TipoEmpresa,
  validarQuestionarioParaTipo,
} from '@contefy/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  centavosParaTexto,
  mascaraCep,
  mascaraCpf,
  mascaraTelefone,
  textoParaCentavos,
} from '../lib/formatos';
import { Alerta, Botao, Campo, Cartao, Titulo } from './ui';

type SocioForm = {
  id?: string;
  nome: string;
  cpf: string;
  telefone: string;
  email: string;
  estado_civil: EstadoCivil;
  regime_bens:
    | ''
    | 'comunhao_parcial'
    | 'comunhao_universal'
    | 'separacao_total'
    | 'participacao_final';
  administrador: boolean;
  percentual_quotas: string;
  nivel_govbr: 'bronze' | 'prata' | 'ouro' | 'nao_sei';
};

type Form = {
  nome1: string;
  nome2: string;
  nome3: string;
  atividades_descricao: string;
  capital_social_centavos: number;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  endereco_e_residencial: boolean;
  inscricao_imobiliaria: string;
  faturamento: '' | 'ate_360k' | 'ate_4_8m' | 'acima';
  tera_funcionarios: boolean;
  socios: SocioForm[];
};

const FATURAMENTO_CENTAVOS: Record<Exclude<Form['faturamento'], ''>, number> = {
  ate_360k: 30_000_000,
  ate_4_8m: 200_000_000,
  acima: 600_000_000,
};

const socioVazio = (administrador: boolean, percentual: string): SocioForm => ({
  nome: '',
  cpf: '',
  telefone: '',
  email: '',
  estado_civil: 'solteiro',
  regime_bens: '',
  administrador,
  percentual_quotas: percentual,
  nivel_govbr: 'nao_sei',
});

function doExistente(
  q: DadosQuestionario | null,
  tipo: TipoEmpresa,
  nomeSugerido: string | null,
): Form {
  if (!q) {
    return {
      nome1: nomeSugerido ?? '',
      nome2: '',
      nome3: '',
      atividades_descricao: '',
      capital_social_centavos: 0,
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      municipio: '',
      uf: '',
      endereco_e_residencial: false,
      inscricao_imobiliaria: '',
      faturamento: '',
      tera_funcionarios: false,
      socios:
        tipo === 'EI'
          ? [socioVazio(true, '100')]
          : [socioVazio(true, '50'), socioVazio(false, '50')],
    };
  }
  const fat = q.faturamento_previsto_anual_centavos;
  return {
    nome1: q.nome_empresarial_opcoes[0] ?? '',
    nome2: q.nome_empresarial_opcoes[1] ?? '',
    nome3: q.nome_empresarial_opcoes[2] ?? '',
    atividades_descricao: q.atividades_descricao,
    capital_social_centavos: q.capital_social_centavos,
    cep: mascaraCep(q.endereco_empresa.cep),
    logradouro: q.endereco_empresa.logradouro,
    numero: q.endereco_empresa.numero,
    complemento: q.endereco_empresa.complemento ?? '',
    bairro: q.endereco_empresa.bairro,
    municipio: q.endereco_empresa.municipio,
    uf: q.endereco_empresa.uf,
    endereco_e_residencial: q.endereco_e_residencial,
    inscricao_imobiliaria: q.inscricao_imobiliaria ?? '',
    faturamento:
      fat == null ? '' : fat <= 36_000_000 ? 'ate_360k' : fat <= 480_000_000 ? 'ate_4_8m' : 'acima',
    tera_funcionarios: q.tera_funcionarios,
    socios: q.socios.map((s) => ({
      id: s.id,
      nome: s.nome,
      cpf: mascaraCpf(s.cpf),
      telefone: mascaraTelefone(s.telefone),
      email: s.email ?? '',
      estado_civil: s.estado_civil,
      regime_bens: s.regime_bens ?? '',
      administrador: s.administrador,
      percentual_quotas: String(s.percentual_quotas),
      nivel_govbr: s.nivel_govbr,
    })),
  };
}

function paraDados(f: Form): unknown {
  return {
    nome_empresarial_opcoes: [f.nome1, f.nome2, f.nome3].map((n) => n.trim()).filter(Boolean),
    atividades_descricao: f.atividades_descricao.trim(),
    capital_social_centavos: f.capital_social_centavos,
    endereco_empresa: {
      cep: f.cep,
      logradouro: f.logradouro.trim(),
      numero: f.numero.trim(),
      complemento: f.complemento.trim(),
      bairro: f.bairro.trim(),
      municipio: f.municipio.trim(),
      uf: f.uf.trim().toUpperCase(),
    },
    endereco_e_residencial: f.endereco_e_residencial,
    inscricao_imobiliaria: f.inscricao_imobiliaria.trim(),
    faturamento_previsto_anual_centavos: f.faturamento ? FATURAMENTO_CENTAVOS[f.faturamento] : null,
    tera_funcionarios: f.tera_funcionarios,
    socios: f.socios.map((s) => ({
      id: s.id,
      nome: s.nome.trim(),
      cpf: s.cpf,
      telefone: s.telefone,
      email: s.email.trim(),
      estado_civil: s.estado_civil,
      regime_bens: s.regime_bens || null,
      administrador: s.administrador,
      percentual_quotas: Number(s.percentual_quotas.replace(',', '.')) || 0,
      nivel_govbr: s.nivel_govbr,
    })),
  };
}

export function QuestionarioForm({
  existente,
  tipoEmpresa,
  estado,
  nomeSugerido,
  bloqueado,
  aoSalvar,
}: {
  existente: DadosQuestionario | null;
  tipoEmpresa: TipoEmpresa;
  estado: string;
  nomeSugerido: string | null;
  bloqueado: boolean;
  aoSalvar: (q: Questionario) => Promise<void>;
}) {
  const [f, setF] = useState<Form>(() => doExistente(existente, tipoEmpresa, nomeSugerido));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(Boolean(existente));
  const [editando, setEditando] = useState(!existente);
  const eEI = tipoEmpresa === 'EI';

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setF((a) => ({ ...a, [k]: v }));
    setSalvo(false);
  };
  const setSocio = (i: number, mudanca: Partial<SocioForm>) => {
    setF((a) => ({ ...a, socios: a.socios.map((s, j) => (j === i ? { ...s, ...mudanca } : s)) }));
    setSalvo(false);
  };

  const buscarCep = async (cep: string) => {
    const d = cep.replace(/\D/g, '');
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const j = (await r.json()) as {
        erro?: boolean;
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
      };
      if (!j.erro) {
        setF((a) => ({
          ...a,
          logradouro: j.logradouro || a.logradouro,
          bairro: j.bairro || a.bairro,
          municipio: j.localidade || a.municipio,
          uf: j.uf || a.uf,
        }));
      }
    } catch {
      /* sem internet ou ViaCEP fora: o cliente preenche à mão */
    }
  };

  const enviar = async () => {
    setErros({});
    setErroGeral(null);
    const dados = paraDados(f);
    const r = questionarioSchema.safeParse(dados);
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const i of r.error.issues) e[i.path.join('.')] = e[i.path.join('.')] ?? i.message;
      setErros(e);
      setErroGeral('Confira os campos destacados.');
      return;
    }
    const erroTipo = validarQuestionarioParaTipo(r.data, tipoEmpresa);
    if (erroTipo) {
      setErroGeral(erroTipo);
      return;
    }
    setSalvando(true);
    try {
      await aoSalvar(r.data);
      setSalvo(true);
      setEditando(false);
    } catch (e) {
      if (e instanceof ErroApi && e.erros.length) {
        const m: Record<string, string> = {};
        for (const x of e.erros) m[x.campo] = x.mensagem;
        setErros(m);
      }
      setErroGeral((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  if (!editando && existente) {
    return (
      <Cartao>
        <Titulo
          sub={
            bloqueado
              ? 'Dados do seu formulário de abertura. Para corrigir algo, fale com a nossa equipe.'
              : 'Respostas recebidas. Você pode ajustar enquanto a equipe não aprovar a ficha.'
          }
        >
          Sobre a empresa e os sócios
        </Titulo>
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <Item rotulo="Nome pretendido" valor={existente.nome_empresarial_opcoes.join(' · ')} />
          <Item
            rotulo="Capital social"
            valor={centavosParaTexto(existente.capital_social_centavos)}
          />
          <Item
            rotulo="Endereço"
            valor={`${existente.endereco_empresa.logradouro}, ${existente.endereco_empresa.numero} · ${existente.endereco_empresa.municipio}/${existente.endereco_empresa.uf}`}
          />
          <Item
            rotulo={eEI ? 'Titular' : 'Sócios'}
            valor={existente.socios.map((s) => `${s.nome} (${s.percentual_quotas}%)`).join(', ')}
          />
        </dl>
        {!bloqueado && (
          <Botao variante="secundario" className="mt-4" onClick={() => setEditando(true)}>
            Ajustar respostas
          </Botao>
        )}
      </Cartao>
    );
  }

  return (
    <Cartao>
      <Titulo sub="Responda com suas palavras. Nossa equipe cuida dos termos técnicos.">
        Sobre a empresa e os sócios
      </Titulo>
      <div className="space-y-6">
        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-slate-900">A empresa</legend>
          <Campo
            rotulo={
              eEI
                ? 'Nome que gostaria de usar (opcional acrescentar à razão social)'
                : 'Nome da empresa (1ª opção)'
            }
            erro={erros.nome_empresarial_opcoes ?? erros['nome_empresarial_opcoes.0']}
            ajuda={
              eEI
                ? 'Para empresário individual, o nome oficial é o seu nome completo; aqui você pode indicar um nome de atividade.'
                : 'A Junta pode recusar nomes parecidos com os de outras empresas; por isso pedimos alternativas.'
            }
          >
            <input
              value={f.nome1}
              onChange={(e) => set('nome1', e.target.value)}
              placeholder="Ex.: Exemplo Tecnologia"
            />
          </Campo>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Campo rotulo="2ª opção de nome">
              <input value={f.nome2} onChange={(e) => set('nome2', e.target.value)} />
            </Campo>
            <Campo rotulo="3ª opção de nome">
              <input value={f.nome3} onChange={(e) => set('nome3', e.target.value)} />
            </Campo>
          </div>
          <Campo
            rotulo="O que a empresa vai fazer?"
            erro={erros.atividades_descricao}
            ajuda="Ex.: vou desenvolver sites e sistemas para pequenas empresas e dar consultoria de marketing digital."
          >
            <textarea
              rows={3}
              value={f.atividades_descricao}
              onChange={(e) => set('atividades_descricao', e.target.value)}
            />
          </Campo>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Campo
              rotulo="Capital social"
              erro={erros.capital_social_centavos}
              ajuda="Valor que os sócios colocam na empresa. Sem centavos. Ex.: R$ 10.000,00"
            >
              <input
                inputMode="numeric"
                value={centavosParaTexto(f.capital_social_centavos)}
                onChange={(e) => set('capital_social_centavos', textoParaCentavos(e.target.value))}
              />
            </Campo>
            <Campo
              rotulo="Quanto espera faturar por ano?"
              ajuda="Uma estimativa. Ajuda a escolher o porte e o regime de impostos."
            >
              <select
                value={f.faturamento}
                onChange={(e) => set('faturamento', e.target.value as Form['faturamento'])}
              >
                <option value="">Não sei ainda</option>
                <option value="ate_360k">Até R$ 360 mil</option>
                <option value="ate_4_8m">Entre R$ 360 mil e R$ 4,8 milhões</option>
                <option value="acima">Acima de R$ 4,8 milhões</option>
              </select>
            </Campo>
          </div>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={f.tera_funcionarios}
              onChange={(e) => set('tera_funcionarios', e.target.checked)}
            />
            <span className="text-sm">A empresa vai ter funcionários já no início</span>
          </label>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-slate-900">
            Endereço da empresa ({estado})
          </legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Campo rotulo="CEP" erro={erros['endereco_empresa.cep']}>
              <input
                inputMode="numeric"
                value={f.cep}
                onChange={(e) => set('cep', mascaraCep(e.target.value))}
                onBlur={(e) => buscarCep(e.target.value)}
              />
            </Campo>
            <div className="sm:col-span-2">
              <Campo rotulo="Rua / Avenida" erro={erros['endereco_empresa.logradouro']}>
                <input value={f.logradouro} onChange={(e) => set('logradouro', e.target.value)} />
              </Campo>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Campo rotulo="Número" erro={erros['endereco_empresa.numero']}>
              <input value={f.numero} onChange={(e) => set('numero', e.target.value)} />
            </Campo>
            <Campo rotulo="Complemento">
              <input
                value={f.complemento}
                onChange={(e) => set('complemento', e.target.value)}
                placeholder="sala, apto"
              />
            </Campo>
            <Campo rotulo="Bairro" erro={erros['endereco_empresa.bairro']}>
              <input value={f.bairro} onChange={(e) => set('bairro', e.target.value)} />
            </Campo>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Campo rotulo="Cidade" erro={erros['endereco_empresa.municipio']}>
                  <input value={f.municipio} onChange={(e) => set('municipio', e.target.value)} />
                </Campo>
              </div>
              <Campo rotulo="UF" erro={erros['endereco_empresa.uf']}>
                <input
                  maxLength={2}
                  value={f.uf}
                  onChange={(e) => set('uf', e.target.value.toUpperCase())}
                />
              </Campo>
            </div>
          </div>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={f.endereco_e_residencial}
              onChange={(e) => set('endereco_e_residencial', e.target.checked)}
            />
            <span className="text-sm">A empresa vai funcionar na casa de um dos sócios</span>
          </label>
          <Campo
            rotulo="Inscrição imobiliária do imóvel (se souber)"
            ajuda="Está no carnê do IPTU. Se não souber, envie o IPTU na lista de documentos e nós encontramos."
          >
            <input
              value={f.inscricao_imobiliaria}
              onChange={(e) => set('inscricao_imobiliaria', e.target.value)}
            />
          </Campo>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="mb-1 text-sm font-semibold text-slate-900">
            {eEI ? 'Titular' : 'Sócios'}
          </legend>
          {erros.socios && <Alerta tipo="erro">{erros.socios}</Alerta>}
          {f.socios.map((s, i) => (
            <div key={s.id ?? i} className="rounded-xl border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold">{eEI ? 'Titular' : `Sócio ${i + 1}`}</p>
                {!eEI && f.socios.length > 1 && (
                  <Botao
                    variante="fantasma"
                    className="text-sm"
                    onClick={() =>
                      set(
                        'socios',
                        f.socios.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" /> Remover
                  </Botao>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Campo
                    rotulo="Nome completo (como está no documento)"
                    erro={erros[`socios.${i}.nome`]}
                  >
                    <input value={s.nome} onChange={(e) => setSocio(i, { nome: e.target.value })} />
                  </Campo>
                </div>
                <Campo rotulo="CPF" erro={erros[`socios.${i}.cpf`]}>
                  <input
                    inputMode="numeric"
                    value={s.cpf}
                    onChange={(e) => setSocio(i, { cpf: mascaraCpf(e.target.value) })}
                  />
                </Campo>
                <Campo
                  rotulo="WhatsApp"
                  erro={erros[`socios.${i}.telefone`]}
                  ajuda="Vamos orientar a assinatura por aqui."
                >
                  <input
                    inputMode="tel"
                    value={s.telefone}
                    onChange={(e) => setSocio(i, { telefone: mascaraTelefone(e.target.value) })}
                  />
                </Campo>
                <Campo rotulo="E-mail" erro={erros[`socios.${i}.email`]}>
                  <input
                    type="email"
                    value={s.email}
                    onChange={(e) => setSocio(i, { email: e.target.value })}
                  />
                </Campo>
                <Campo rotulo="Estado civil" erro={erros[`socios.${i}.estado_civil`]}>
                  <select
                    value={s.estado_civil}
                    onChange={(e) =>
                      setSocio(i, { estado_civil: e.target.value as EstadoCivil, regime_bens: '' })
                    }
                  >
                    <option value="solteiro">Solteiro(a)</option>
                    <option value="casado">Casado(a)</option>
                    <option value="uniao_estavel">União estável</option>
                    <option value="divorciado">Divorciado(a)</option>
                    <option value="viuvo">Viúvo(a)</option>
                  </select>
                </Campo>
                {(s.estado_civil === 'casado' || s.estado_civil === 'uniao_estavel') && (
                  <Campo
                    rotulo="Regime de bens"
                    erro={erros[`socios.${i}.regime_bens`]}
                    ajuda="Está na certidão de casamento."
                  >
                    <select
                      value={s.regime_bens}
                      onChange={(e) =>
                        setSocio(i, { regime_bens: e.target.value as SocioForm['regime_bens'] })
                      }
                    >
                      <option value="">Não sei</option>
                      <option value="comunhao_parcial">Comunhão parcial</option>
                      <option value="comunhao_universal">Comunhão universal</option>
                      <option value="separacao_total">Separação total</option>
                      <option value="participacao_final">Participação final nos aquestos</option>
                    </select>
                  </Campo>
                )}
                {!eEI && (
                  <Campo rotulo="Participação (%)" erro={erros[`socios.${i}.percentual_quotas`]}>
                    <input
                      inputMode="decimal"
                      value={s.percentual_quotas}
                      onChange={(e) => setSocio(i, { percentual_quotas: e.target.value })}
                    />
                  </Campo>
                )}
                <Campo
                  rotulo="Conta gov.br"
                  ajuda={
                    estado === 'SP'
                      ? 'Em São Paulo a assinatura exige nível Ouro.'
                      : 'A assinatura exige nível Prata ou Ouro.'
                  }
                >
                  <select
                    value={s.nivel_govbr}
                    onChange={(e) =>
                      setSocio(i, { nivel_govbr: e.target.value as SocioForm['nivel_govbr'] })
                    }
                  >
                    <option value="nao_sei">Não sei o nível</option>
                    <option value="bronze">Bronze</option>
                    <option value="prata">Prata</option>
                    <option value="ouro">Ouro</option>
                  </select>
                </Campo>
                {!eEI && (
                  <label className="flex items-center gap-3 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={s.administrador}
                      onChange={(e) => setSocio(i, { administrador: e.target.checked })}
                    />
                    <span className="text-sm">Vai administrar a empresa (assina pela empresa)</span>
                  </label>
                )}
              </div>
            </div>
          ))}
          {!eEI && f.socios.length < 10 && (
            <Botao
              variante="secundario"
              onClick={() => set('socios', [...f.socios, socioVazio(false, '0')])}
            >
              <Plus className="h-4 w-4" /> Adicionar sócio
            </Botao>
          )}
        </fieldset>

        {erroGeral && <Alerta tipo="erro">{erroGeral}</Alerta>}
        {salvo && !erroGeral && <Alerta tipo="sucesso">Respostas salvas.</Alerta>}
        <div className="flex flex-wrap gap-2">
          <Botao
            className="flex-1 sm:flex-none"
            carregando={salvando}
            onClick={enviar}
            disabled={bloqueado}
          >
            Salvar respostas
          </Botao>
          {existente && (
            <Botao variante="secundario" onClick={() => setEditando(false)}>
              Cancelar
            </Botao>
          )}
        </div>
      </div>
    </Cartao>
  );
}

function Item({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{rotulo}</dt>
      <dd className="text-slate-800">{valor}</dd>
    </div>
  );
}
